import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API = 'https://groovie-business-api-production.up.railway.app/api/v1';

const STATUSES = [
  { value: 'draft', label: 'Draft', bg: '#f3f4f6', color: '#374151' },
  { value: 'active', label: 'Active', bg: '#ecfdf5', color: '#059669' },
  { value: 'paused', label: 'Paused', bg: '#fffbeb', color: '#d97706' },
  { value: 'completed', label: 'Completed', bg: '#eff6ff', color: '#2563eb' },
  { value: 'expired', label: 'Expired', bg: '#fef2f2', color: '#dc2626' },
];

const FREQUENCIES = [
  { value: 'hourly', label: 'Every hour' }, { value: 'every_2h', label: 'Every 2 hours' },
  { value: 'every_4h', label: 'Every 4 hours' }, { value: 'daily', label: 'Once daily' },
  { value: 'custom', label: 'Custom time slots' },
];

const RATE_TYPES = [
  { value: 'flat', label: 'Flat rate' }, { value: 'per_play', label: 'Per play' },
  { value: 'per_day', label: 'Per day' }, { value: 'per_week', label: 'Per week' },
];

const VOICES = [
  { value: 'male_us', label: 'US Voice — Male' }, { value: 'female_us', label: 'US Voice — Female' },
  { value: 'male_us_2', label: 'US Voice — Male (alt)' }, { value: 'female_us_2', label: 'US Voice — Female (alt)' },
  { value: 'male_uk', label: 'UK Voice — Male' }, { value: 'female_uk', label: 'UK Voice — Female' },
];

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const DEFAULT_FORM = {
  advertiser_name: '', campaign_name: '', contact_email: '', contact_phone: '',
  ad_copy: '', duration: 30, start_date: '', end_date: '',
  frequency: 'hourly', time_slots: [], days_of_week: [0,1,2,3,4,5,6],
  channel_id: null, rate_type: 'flat', rate_amount: 0, total_budget: 0, priority: 'normal'
};

const Svg = ({ type, size = 16 }) => {
  const icons = {
    play: <polygon points="5,3 19,12 5,21" fill="currentColor" />,
    stop: <rect x="4" y="4" width="16" height="16" rx="2" fill="currentColor" />,
    plus: <><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>,
    edit: <><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></>,
    trash: <><polyline points="3,6 5,6 21,6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></>,
    mic: <><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/></>,
    volume: <><polygon points="11,5 6,9 2,9 2,15 6,15 11,19"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></>,
    megaphone: <><path d="m3 11 18-5v12L3 13v-2z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></>,
    calendar: <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>,
    dollar: <><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></>,
    clock: <><circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/></>,
    check: <><polyline points="20,6 9,17 4,12"/></>,
    pause: <><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></>,
    zap: <><polygon points="13,2 3,14 12,14 11,22 21,10 12,10"/></>,
    user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{icons[type]}</svg>
  );
};

const inputStyle = {
  width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 10,
  padding: '11px 14px', fontSize: 14, outline: 'none',
  fontFamily: "'Outfit', sans-serif", boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};
const smallInput = { ...inputStyle, padding: '9px 12px', fontSize: 13 };
const focusIn = (e) => e.target.style.borderColor = '#7c3aed';
const focusOut = (e) => e.target.style.borderColor = '#e5e7eb';

export default function Ads() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ ...DEFAULT_FORM });
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [adVoice, setAdVoice] = useState('female_us');
  const [playingId, setPlayingId] = useState(null);
  const [audioRef] = useState({ current: null });
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}` };
  const jsonHeaders = { ...headers, 'Content-Type': 'application/json' };

  useEffect(() => { if (!token) { navigate('/login'); return; } fetchCampaigns(); fetchStats(); }, [token, navigate]);

  const fetchCampaigns = async () => {
    try { setLoading(true); const res = await fetch(`${API}/ads`, { headers }); if (res.status === 401) { navigate('/login'); return; } if (res.ok) { const d = await res.json(); setCampaigns(d.campaigns || []); } } catch { setCampaigns([]); } finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try { const res = await fetch(`${API}/ads/stats`, { headers }); if (res.ok) setStats(await res.json()); } catch {}
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const body = { ...formData }; if (!body.channel_id) delete body.channel_id;
      const res = await fetch(editing ? `${API}/ads/${editingId}` : `${API}/ads`, { method: editing ? 'PUT' : 'POST', headers: jsonHeaders, body: JSON.stringify(body) });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Failed'); }
      await fetchCampaigns(); await fetchStats(); closeModal();
    } catch (err) { alert('Failed: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete campaign "${name}"?`)) return;
    try { const res = await fetch(`${API}/ads/${id}`, { method: 'DELETE', headers }); if (res.ok) { await fetchCampaigns(); await fetchStats(); } } catch { alert('Failed'); }
  };

  const handleStatusChange = async (id, status) => {
    try { const res = await fetch(`${API}/ads/${id}/status?status=${status}`, { method: 'PUT', headers }); if (res.ok) { await fetchCampaigns(); await fetchStats(); } } catch { alert('Failed'); }
  };

  const handleGenerateAudio = async (id) => {
    setGenerating(id);
    try { const res = await fetch(`${API}/ads/${id}/generate-audio?voice=${adVoice}`, { method: 'POST', headers }); if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Failed'); } await fetchCampaigns(); } catch (err) { alert('Audio generation failed: ' + err.message); } finally { setGenerating(null); }
  };

  const openEdit = (c) => {
    setFormData({
      advertiser_name: c.advertiser_name || '', campaign_name: c.campaign_name || '',
      contact_email: c.contact_email || '', contact_phone: c.contact_phone || '',
      ad_copy: c.ad_copy || '', duration: c.duration || 30,
      start_date: c.start_date ? c.start_date.slice(0, 16) : '', end_date: c.end_date ? c.end_date.slice(0, 16) : '',
      frequency: c.frequency || 'hourly', time_slots: c.time_slots || [],
      days_of_week: c.days_of_week?.length ? c.days_of_week : [0,1,2,3,4,5,6],
      channel_id: c.channel_id || null, rate_type: c.rate_type || 'flat',
      rate_amount: c.rate_amount || 0, total_budget: c.total_budget || 0, priority: c.priority || 'normal'
    });
    setEditing(true); setEditingId(c.id); setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditing(false); setEditingId(null); setFormData({ ...DEFAULT_FORM }); };

  const playAudio = (url, id) => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; if (playingId === id) { setPlayingId(null); return; } }
    const a = new Audio(url); a.onended = () => setPlayingId(null); a.play(); audioRef.current = a; setPlayingId(id);
  };

  const toggleDay = (i) => {
    const days = [...formData.days_of_week]; const idx = days.indexOf(i);
    if (idx >= 0) days.splice(idx, 1); else days.push(i);
    setFormData({ ...formData, days_of_week: days.sort() });
  };

  const getStatusInfo = (s) => STATUSES.find(st => st.value === s) || STATUSES[0];
  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  const daysRemaining = (end) => { if (!end) return null; return Math.ceil((new Date(end) - new Date()) / (1000 * 60 * 60 * 24)); };
  const filtered = filterStatus === 'all' ? campaigns : campaigns.filter(c => c.status === filterStatus);

  if (loading) return (
    <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: 48, height: 48, borderRadius: '50%', border: '3px solid #e9d5ff', borderTopColor: '#7c3aed', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1200, margin: '0 auto', fontFamily: "'Outfit', 'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-0.5px' }}>Ad Campaigns</h1>
          <p style={{ fontSize: 14, color: '#9ca3af', marginTop: 4 }}>Manage advertiser campaigns for your radio channels</p>
        </div>
        <button onClick={() => { setFormData({ ...DEFAULT_FORM }); setEditing(false); setShowModal(true); }} style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'linear-gradient(135deg, #7c3aed, #a855f7)', color: '#fff',
          border: 'none', borderRadius: 12, padding: '12px 24px', fontSize: 14, fontWeight: 600,
          cursor: 'pointer', fontFamily: "'Outfit', sans-serif",
          boxShadow: '0 4px 12px rgba(124,58,237,0.3)', transition: 'transform 0.15s',
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'none'}
        ><Svg type="plus" size={16} /> New Campaign</button>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
          {[
            { label: 'Active Campaigns', value: stats.active_campaigns, color: '#059669', icon: 'zap' },
            { label: 'Total Revenue', value: `$${stats.total_revenue?.toLocaleString() || 0}`, color: '#111827', icon: 'dollar' },
            { label: 'Total Plays', value: stats.total_plays?.toLocaleString() || 0, color: '#7c3aed', icon: 'play' },
            { label: 'Drafts / Completed', value: `${stats.draft_campaigns} / ${stats.completed_campaigns}`, color: '#6b7280', icon: 'check' },
          ].map((s, i) => (
            <div key={i} style={{
              background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', padding: '18px 20px',
              transition: 'box-shadow 0.15s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.05)'}
            onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: `${s.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.color }}><Svg type={s.icon} size={14} /></div>
                <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</span>
              </div>
              <div style={{ fontSize: 24, fontWeight: 800, color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: 150 }}>
          <option value="all">All Status</option>
          {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={adVoice} onChange={(e) => setAdVoice(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: 200 }}>
          {VOICES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
        </select>
        <span style={{ fontSize: 13, color: '#9ca3af', marginLeft: 'auto' }}>{filtered.length} campaigns</span>
      </div>

      {/* Campaign List */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '64px 24px', background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb' }}>
          <div style={{ color: '#9ca3af', marginBottom: 16 }}><Svg type="megaphone" size={48} /></div>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: '#374151', margin: '0 0 8px' }}>
            {campaigns.length === 0 ? 'No ad campaigns yet' : 'No matching campaigns'}
          </h3>
          <p style={{ fontSize: 14, color: '#9ca3af', margin: '0 0 24px' }}>Create campaigns for local advertisers and generate audio automatically.</p>
          <button onClick={() => { setFormData({ ...DEFAULT_FORM }); setEditing(false); setShowModal(true); }} style={{
            padding: '12px 28px', borderRadius: 10, fontSize: 14, fontWeight: 600,
            border: 'none', background: 'linear-gradient(135deg, #7c3aed, #a855f7)', color: '#fff', cursor: 'pointer',
          }}>New Campaign</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(c => {
            const statusInfo = getStatusInfo(c.status);
            const days = daysRemaining(c.end_date);
            const hasAudio = !!c.audio_url;
            return (
              <div key={c.id} style={{
                background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb',
                overflow: 'hidden', transition: 'box-shadow 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
              >
                <div style={{ padding: '18px 22px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: 0 }}>{c.campaign_name}</h3>
                        <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: statusInfo.bg, color: statusInfo.color }}>{statusInfo.label}</span>
                        {hasAudio && <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#ecfdf5', color: '#059669', display: 'flex', alignItems: 'center', gap: 3 }}><Svg type="volume" size={9} /> Audio</span>}
                        {!hasAudio && c.ad_copy && <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#fffbeb', color: '#d97706' }}>No audio</span>}
                        {c.priority === 'high' && <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#fff7ed', color: '#ea580c', display: 'flex', alignItems: 'center', gap: 3 }}><Svg type="zap" size={9} /> High</span>}
                      </div>
                      <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 8px' }}>
                        <span style={{ fontWeight: 600 }}>{c.advertiser_name}</span>
                        {c.contact_email && <span style={{ color: '#9ca3af', marginLeft: 8 }}>&middot; {c.contact_email}</span>}
                      </p>
                      {c.ad_copy && (
                        <div style={{ background: '#f9fafb', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: '#6b7280', marginBottom: 8, fontStyle: 'italic', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          "{c.ad_copy}"
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 11, color: '#9ca3af', flexWrap: 'wrap' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Svg type="calendar" size={10} /> {formatDate(c.start_date)} &rarr; {formatDate(c.end_date)}</span>
                        {days !== null && days > 0 && c.status === 'active' && <span style={{ color: '#059669', fontWeight: 600 }}>{days}d remaining</span>}
                        {days !== null && days <= 0 && c.status === 'active' && <span style={{ color: '#dc2626', fontWeight: 600 }}>Expired</span>}
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Svg type="clock" size={10} /> {FREQUENCIES.find(f => f.value === c.frequency)?.label || c.frequency}</span>
                        <span>{c.duration}s</span>
                        {c.total_budget > 0 && <span style={{ color: '#059669', fontWeight: 600 }}>${c.total_budget.toLocaleString()}</span>}
                        {c.plays_delivered > 0 && <span>{c.plays_delivered} plays</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, flexWrap: 'wrap' }}>
                      {c.ad_copy && !hasAudio && (
                        <button onClick={() => handleGenerateAudio(c.id)} disabled={generating === c.id} style={{
                          display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8,
                          fontSize: 12, fontWeight: 600, border: 'none', cursor: generating === c.id ? 'not-allowed' : 'pointer',
                          background: '#eff6ff', color: '#2563eb', opacity: generating === c.id ? 0.6 : 1,
                        }}><Svg type="mic" size={12} /> {generating === c.id ? 'Gen...' : 'Generate'}</button>
                      )}
                      {hasAudio && (
                        <button onClick={() => playAudio(c.audio_url, c.id)} style={{
                          display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8,
                          fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                          background: playingId === c.id ? '#7c3aed' : '#f5f3ff',
                          color: playingId === c.id ? '#fff' : '#7c3aed',
                        }}><Svg type={playingId === c.id ? 'stop' : 'play'} size={12} /> {playingId === c.id ? 'Stop' : 'Play'}</button>
                      )}
                      {c.status === 'draft' && hasAudio && (
                        <button onClick={() => handleStatusChange(c.id, 'active')} style={{
                          display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8,
                          fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', background: '#ecfdf5', color: '#059669',
                        }}><Svg type="zap" size={12} /> Activate</button>
                      )}
                      {c.status === 'active' && (
                        <button onClick={() => handleStatusChange(c.id, 'paused')} style={{ padding: 7, borderRadius: 8, border: 'none', cursor: 'pointer', background: '#fffbeb', color: '#d97706' }}><Svg type="pause" size={14} /></button>
                      )}
                      {c.status === 'paused' && (
                        <button onClick={() => handleStatusChange(c.id, 'active')} style={{ padding: 7, borderRadius: 8, border: 'none', cursor: 'pointer', background: '#ecfdf5', color: '#059669' }}><Svg type="play" size={14} /></button>
                      )}
                      {(c.status === 'active' || c.status === 'paused') && (
                        <button onClick={() => handleStatusChange(c.id, 'completed')} style={{ padding: 7, borderRadius: 8, border: 'none', cursor: 'pointer', background: '#eff6ff', color: '#2563eb' }}><Svg type="check" size={14} /></button>
                      )}
                      <button onClick={() => openEdit(c)} style={{ padding: 7, borderRadius: 8, border: 'none', cursor: 'pointer', background: 'transparent', color: '#9ca3af' }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#7c3aed'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
                      ><Svg type="edit" size={14} /></button>
                      <button onClick={() => handleDelete(c.id, c.campaign_name)} style={{ padding: 7, borderRadius: 8, border: 'none', cursor: 'pointer', background: 'transparent', color: '#d1d5db' }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#d1d5db'}
                      ><Svg type="trash" size={14} /></button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MODAL */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16, backdropFilter: 'blur(4px)' }}>
          <div style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', padding: 32, boxShadow: '0 25px 60px rgba(0,0,0,0.15)' }}>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 24px' }}>{editing ? 'Edit Campaign' : 'New Ad Campaign'}</h2>
            <form onSubmit={handleSave}>
              {/* Advertiser */}
              <div style={{ background: '#f9fafb', borderRadius: 12, padding: 18, border: '1px solid #e5e7eb', marginBottom: 18 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#374151', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 6 }}><Svg type="user" size={14} /> Advertiser</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Advertiser Name *</label>
                    <input type="text" required value={formData.advertiser_name} onChange={(e) => setFormData({ ...formData, advertiser_name: e.target.value })}
                      placeholder="Joe's Pizza" style={smallInput} onFocus={focusIn} onBlur={focusOut} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Campaign Name *</label>
                    <input type="text" required value={formData.campaign_name} onChange={(e) => setFormData({ ...formData, campaign_name: e.target.value })}
                      placeholder="Summer Special" style={smallInput} onFocus={focusIn} onBlur={focusOut} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Email</label>
                    <input type="email" value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      placeholder="joe@pizza.com" style={smallInput} onFocus={focusIn} onBlur={focusOut} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Phone</label>
                    <input type="text" value={formData.contact_phone} onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      placeholder="(555) 123-4567" style={smallInput} onFocus={focusIn} onBlur={focusOut} />
                  </div>
                </div>
              </div>

              {/* Ad Copy */}
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Ad Copy <span style={{ color: '#9ca3af', fontWeight: 400 }}>&mdash; script read on air</span>
                </label>
                <textarea rows={3} value={formData.ad_copy} onChange={(e) => setFormData({ ...formData, ad_copy: e.target.value })}
                  placeholder="Looking for the best pizza in town? Come to Joe's Pizza..."
                  style={{ ...inputStyle, resize: 'vertical' }} onFocus={focusIn} onBlur={focusOut} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>{formData.ad_copy.length} chars</span>
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>~{Math.max(Math.ceil(formData.ad_copy.split(' ').filter(Boolean).length / 2.5), 0)}s at natural pace</span>
                </div>
              </div>

              {/* Schedule */}
              <div style={{ background: '#eff6ff', borderRadius: 12, padding: 18, border: '1px solid #bfdbfe', marginBottom: 18 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#1e40af', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 6 }}><Svg type="calendar" size={14} /> Schedule</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Start Date *</label>
                    <input type="datetime-local" required value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} style={smallInput} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>End Date *</label>
                    <input type="datetime-local" required value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} style={smallInput} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Frequency</label>
                    <select value={formData.frequency} onChange={(e) => setFormData({ ...formData, frequency: e.target.value })} style={{ ...smallInput, background: '#fff' }}>
                      {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Duration (sec)</label>
                    <input type="number" min={5} max={120} value={formData.duration} onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 30 })} style={smallInput} />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Days</label>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {DAYS.map((d, i) => (
                      <button key={i} type="button" onClick={() => toggleDay(i)} style={{
                        padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                        border: 'none', cursor: 'pointer',
                        background: formData.days_of_week.includes(i) ? '#2563eb' : '#fff',
                        color: formData.days_of_week.includes(i) ? '#fff' : '#6b7280',
                      }}>{d}</button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Financials */}
              <div style={{ background: '#ecfdf5', borderRadius: 12, padding: 18, border: '1px solid #bbf7d0', marginBottom: 18 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#166534', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 6 }}><Svg type="dollar" size={14} /> Financials</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Rate Type</label>
                    <select value={formData.rate_type} onChange={(e) => setFormData({ ...formData, rate_type: e.target.value })} style={{ ...smallInput, background: '#fff' }}>
                      {RATE_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Rate ($)</label>
                    <input type="number" min={0} step={0.01} value={formData.rate_amount} onChange={(e) => setFormData({ ...formData, rate_amount: parseFloat(e.target.value) || 0 })} style={smallInput} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Total Budget ($)</label>
                    <input type="number" min={0} step={0.01} value={formData.total_budget} onChange={(e) => setFormData({ ...formData, total_budget: parseFloat(e.target.value) || 0 })} style={smallInput} />
                  </div>
                </div>
              </div>

              {/* Priority */}
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Priority</label>
                <select value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })} style={{ ...smallInput, width: 'auto', minWidth: 140, background: '#fff' }}>
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: 12, borderTop: '1px solid #f3f4f6', paddingTop: 20 }}>
                <button type="button" onClick={closeModal} style={{
                  flex: 1, padding: '12px 20px', borderRadius: 10, border: '1.5px solid #e5e7eb',
                  background: '#fff', color: '#374151', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: "'Outfit', sans-serif",
                }}>Cancel</button>
                <button type="submit" disabled={saving} style={{
                  flex: 1, padding: '12px 20px', borderRadius: 10, border: 'none',
                  background: saving ? '#c4b5fd' : 'linear-gradient(135deg, #7c3aed, #a855f7)',
                  color: '#fff', fontSize: 14, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
                  fontFamily: "'Outfit', sans-serif", boxShadow: '0 4px 12px rgba(124,58,237,0.3)',
                }}>{saving ? 'Saving...' : (editing ? 'Save Changes' : 'Create Campaign')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}